// PWA install prompt + dynamic web-app manifest.
//
// Why this exists
// ───────────────
// Each deployment of this system is branded to a specific facility (Dekyi,
// Zunuo, Jitrock, …). The static `public/manifest.json` shipped at build time
// says "Hospital Management System", which means a user who installs the PWA
// gets a desktop icon labelled with the generic name instead of their
// facility's. This component replaces the manifest at runtime with one
// generated from the live `companyInfo` payload so the OS shortcut shows the
// facility name + the facility logo, and a small install card appears in the
// top-right inviting the user to add the app to their desktop.
//
// Flow
// ────
//   1. On mount we listen for the browser's `beforeinstallprompt` event and
//      stash the deferred prompt object so we can fire it on demand.
//   2. As soon as `companyInfo` is available we build a manifest JSON
//      (correct name + icon URLs), wrap it in a Blob URL, and swap the
//      <link rel="manifest"> href to point at it. Chromium reads the new
//      manifest right away, so any subsequent install uses the facility
//      branding instead of the generic one.
//   3. When (a) we have a deferred prompt and (b) the app isn't already
//      running standalone and (c) the user hasn't recently dismissed the
//      prompt, we render a card in the top-right corner. Clicking Install
//      fires `prompt()` on the deferred event; clicking "Not now" remembers
//      a dismissal for 7 days so we don't pester them every session.
//   4. We also listen for `appinstalled` so the card disappears immediately
//      after a successful install without waiting for a refresh.
//
// Browsers without `beforeinstallprompt` (Safari, Firefox desktop) simply
// never see the card. That's fine — installing on those is a manual menu
// action; we surface nothing rather than something broken.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Button, IconButton, Typography, Slide, Avatar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const DISMISS_KEY = 'pwa.installDismissedAt';
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MANIFEST_LINK_ID = 'app-manifest';

const firstWord = (s) => (s || '').toString().trim().split(/\s+/)[0] || '';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  // iOS Safari uses `navigator.standalone`; Chromium uses the display-mode
  // media query. Cover both so we never offer to install an already-installed
  // app to a user who's launched it from the home-screen icon.
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator && window.navigator.standalone) return true;
  return false;
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return (Date.now() - at) < DISMISS_WINDOW_MS;
  } catch (_) {
    return false;
  }
}

function rememberDismissal() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* non-fatal */ }
}

function inferIconType(url) {
  if (!url) return 'image/png';
  const lower = String(url).toLowerCase().split('?')[0];
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.ico'))  return 'image/x-icon';
  return 'image/png';
}

// Build a manifest JSON tailored to the facility and inject it as a Blob URL.
// We hold onto the previous Blob URL so we can revoke it on the next swap and
// not leak memory across re-renders.
function applyDynamicManifest(company, blobUrlRef) {
  if (!company || typeof document === 'undefined') return;

  const name = (company.name || 'Hospital').toString().trim();
  const shortName = firstWord(name) || 'Hospital';
  const themeColor = company.theme_color || '#1e6091';
  const backgroundColor = company.background_color || '#ffffff';

  const icons = [];
  if (company.logo) {
    const type = inferIconType(company.logo);
    icons.push({ src: company.logo, sizes: '192x192', type, purpose: 'any' });
    icons.push({ src: company.logo, sizes: '512x512', type, purpose: 'any maskable' });
  } else {
    // Fall back to the bundled logos so a manifest is always installable
    // even when the facility hasn't uploaded a logo yet.
    icons.push({ src: '/logo192.png', sizes: '192x192', type: 'image/png' });
    icons.push({ src: '/logo512.png', sizes: '512x512', type: 'image/png' });
  }

  const manifest = {
    name,
    short_name: shortName,
    description: company.mmda || `${name} — Management System`,
    icons,
    start_url: '/',
    scope: '/',
    display: 'standalone',
    orientation: 'any',
    theme_color: themeColor,
    background_color: backgroundColor,
  };

  let blob;
  try {
    blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  } catch (_) {
    return;
  }
  const url = URL.createObjectURL(blob);

  let link = document.getElementById(MANIFEST_LINK_ID);
  if (!link) {
    // The existing link in index.html doesn't have our id; find it by rel
    // and tag it so future swaps reuse the same element.
    link = document.querySelector('link[rel="manifest"]');
    if (link) link.id = MANIFEST_LINK_ID;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    link.id = MANIFEST_LINK_ID;
    document.head.appendChild(link);
  }

  // Manifest blob URLs need the crossorigin attribute on some browsers when
  // the page itself is loaded under a strict policy — setting it is harmless
  // when the origin matches.
  link.setAttribute('crossorigin', 'use-credentials');
  link.href = url;

  // Revoke the previous blob (if any) on the next tick — revoking immediately
  // can race with the browser's manifest fetch.
  const previous = blobUrlRef.current;
  blobUrlRef.current = url;
  if (previous) {
    setTimeout(() => { try { URL.revokeObjectURL(previous); } catch (_) {} }, 4000);
  }
}

export default function PWAInstallProvider({ companyInfo }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => recentlyDismissed());
  const blobUrlRef = useRef(null);

  // Capture the install prompt the moment the browser raises it and stop the
  // default mini-bar so we can offer our own card with the facility's name.
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Keep the manifest in sync with whatever companyInfo we know about. We
  // pull from props first (the App component fetches it fresh) and fall back
  // to whatever's cached in localStorage so the first paint after a hard
  // refresh still uses facility branding.
  useEffect(() => {
    let company = companyInfo;
    if (!company) {
      try {
        const raw = localStorage.getItem('companyInfo');
        if (raw) company = JSON.parse(raw);
      } catch (_) {}
    }
    if (company) applyDynamicManifest(company, blobUrlRef);
  }, [companyInfo]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      // Whether accepted or dismissed, the deferred prompt can only be used
      // once. Clear it so the card disappears.
      setDeferredPrompt(null);
      if (choice && choice.outcome === 'dismissed') {
        rememberDismissal();
        setDismissed(true);
      }
    } catch (_) {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    rememberDismissal();
    setDismissed(true);
  }, []);

  // Don't render anything if we have nothing to offer or the user has
  // already passed on the prompt this week.
  const show = !!deferredPrompt && !installed && !dismissed && !isStandalone();
  if (!show) return null;

  const name = (companyInfo?.name || 'this application').toString().trim();
  const tagline = companyInfo?.mmda || 'Install for faster access and offline launch';
  const logo = companyInfo?.logo;

  return (
    <Slide in direction="left">
      <Box
        role="dialog"
        aria-label="Install application"
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: (t) => t.zIndex.snackbar + 2,
          width: 340,
          maxWidth: 'calc(100vw - 32px)',
          bgcolor: 'background.paper',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 18px 40px rgba(15,23,42,0.18)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #1e6091 0%, #2475a8 100%)',
          color: '#fff',
          px: 1.75,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}>
          <Avatar
            src={logo || undefined}
            alt=""
            sx={{
              bgcolor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              width: 36,
              height: 36,
              fontWeight: 700,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          >
            {!logo && firstWord(name).charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.15 }} noWrap>
              Install {name}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', opacity: 0.88 }} noWrap>
              Add to desktop · launches in its own window
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleDismiss} aria-label="dismiss" sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 1.75 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem', mb: 1.5 }}>
            {tagline}. Installing gives you a desktop shortcut with the {firstWord(name) || 'facility'} logo and
            opens the system in its own window without browser tabs.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={handleDismiss}>Not now</Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={handleInstall}
              sx={{
                background: 'linear-gradient(135deg, #1e6091 0%, #2475a8 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #195075 0%, #1e6091 100%)' },
              }}
            >
              Install
            </Button>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
}
